import { useState, useMemo } from 'react';
import MetricCard from '../shared/MetricCard';
import Badge from '../shared/Badge';
import DataTable from '../shared/DataTable';
import EmptyState from '../shared/EmptyState';
import { formatMoney, formatDate } from '../../utils/formatters';

export default function CodReconciliation({ data }) {
  const [tab, setTab] = useState('orders');

  const orders = data?.orders || [];
  const mismatches = data?.mismatches || [];
  const unpaidCount = data?.unpaid_count || 0;
  const totalUnpaid = data?.total_unpaid_usd || 0;

  const orderColumns = useMemo(() => [
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => <span className="font-semibold text-sm">{row.vendor_name}</span>,
    },
    {
      key: 'po_number',
      label: 'PO #',
      render: (row) => <span className="font-mono text-xs">#{row.po_number}</span>,
    },
    {
      key: 'ro_number',
      label: 'RO #',
      render: (row) => <span className="font-semibold text-sm">#{row.ro_number}</span>,
    },
    {
      key: 'order_date',
      label: 'Order Date',
      render: (row) => <span className="text-xs">{formatDate(row.order_date)}</span>,
    },
    {
      key: 'ap_total_usd',
      label: 'AP Total',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.ap_total_usd)}</span>,
    },
    {
      key: 'payment_status',
      label: 'Status',
      render: (row) => {
        const isPaid = row.payment_status === 'PAID';
        return <Badge severity={isPaid ? 'ok' : 'warning'} text={row.payment_status || 'UNPAID'} />;
      },
    },
  ], []);

  const mismatchColumns = useMemo(() => [
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => <span className="font-semibold text-sm">{row.vendor_name}</span>,
    },
    {
      key: 'po_number',
      label: 'PO #',
      render: (row) => <span className="font-mono text-xs">#{row.po_number}</span>,
    },
    {
      key: 'calculated_total_usd',
      label: 'Calculated',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.calculated_total_usd)}</span>,
    },
    {
      key: 'recorded_total_usd',
      label: 'Recorded',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.recorded_total_usd)}</span>,
    },
    {
      key: 'discrepancy_usd',
      label: 'Discrepancy',
      align: 'right',
      render: (row) => {
        const disc = row.discrepancy_usd || 0;
        return (
          <span className={`font-extrabold ${disc > 0 ? 'text-red-700' : 'text-green-700'}`}>
            {disc > 0 ? '+' : ''}{formatMoney(disc)}
          </span>
        );
      },
    },
  ], []);

  return (
    <div>
      <div className="flex gap-3 mb-6 flex-wrap">
        <MetricCard
          label="COD Orders"
          value={orders.length.toString()}
          accent="#22C55E"
          icon={'\uD83D\uDCB5'}
        />
        <MetricCard
          label="Unpaid"
          value={unpaidCount.toString()}
          accent="#E65100"
        />
        <MetricCard
          label="Total Unpaid"
          value={formatMoney(totalUnpaid)}
          accent="#C62828"
        />
        <MetricCard
          label="Mismatches"
          value={mismatches.length.toString()}
          sub="Payment vs recorded total"
          accent="#DC2626"
        />
      </div>

      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('orders')}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            tab === 'orders' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Orders ({orders.length})
        </button>
        <button
          onClick={() => setTab('mismatches')}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            tab === 'mismatches' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Mismatches ({mismatches.length})
        </button>
      </div>

      {tab === 'orders' ? (
        orders.length > 0 ? <DataTable columns={orderColumns} data={orders} /> : <EmptyState text="No COD orders found" />
      ) : (
        mismatches.length > 0 ? <DataTable columns={mismatchColumns} data={mismatches} /> : <EmptyState text="No payment mismatches" />
      )}
    </div>
  );
}
